import React, { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "motion/react";

/**
 * A gold pulse cursor that follows the mouse.
 * Grows on interactive elements (buttons, links).
 */
const CustomCursor = () => {
  const [hovering, setHovering] = useState(false);
  const [visible, setVisible] = useState(false);
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);
  const springX = useSpring(mouseX, { stiffness: 400, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 400, damping: 30 });
  const ringX = useSpring(mouseX, { stiffness: 120, damping: 15 });
  const ringY = useSpring(mouseY, { stiffness: 120, damping: 15 });

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Bail on touch devices
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const onMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      setVisible(true);
    };
    const onOver = (e) => {
      const t = e.target;
      if (!t) return;
      const el =
        t.closest &&
        t.closest("a, button, [role='button'], input, textarea, select");
      setHovering(!!el);
    };
    const onLeave = () => setVisible(false);
    document.body.classList.add("cursor-none");
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      document.body.classList.remove("cursor-none");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, [mouseX, mouseY]);

  if (!visible) return null;

  return (
    <>
      <motion.div
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[9999] mix-blend-difference"
        style={{ x: springX, y: springY, translateX: "-50%", translateY: "-50%" }}
      >
        <div className="w-2 h-2 rounded-full bg-white" />
      </motion.div>
      <motion.div
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[9998]"
        style={{ x: ringX, y: ringY, translateX: "-50%", translateY: "-50%" }}
        animate={{ scale: hovering ? 2.5 : 1, opacity: hovering ? 0.6 : 0.4 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <div className="w-9 h-9 rounded-full border border-gold" />
      </motion.div>
    </>
  );
};

export default CustomCursor;
